import React from "react";
import { AlertTriangle, Check } from "lucide-react";
import { Inspector } from "@/components/canvas/Inspector";
import { BasisMark } from "@/components/canvas/BasisMark";
import { RisikoCard } from "@/components/canvas/risiko/RisikoCard";
import { basisOf } from "@/lib/sk";

const chip = "rounded-md bg-white px-1.5 py-0.5 font-mono text-[11.5px] text-[hsl(var(--brand-strong))] shadow-[0_0_0_1px_hsl(212_40%_16%/0.08)] hover:underline";
const mini = "rounded-full bg-[hsl(var(--brand))] px-1.5 text-[10.5px] font-medium text-white hover:bg-[hsl(var(--brand-strong))]";

/** Everything that stands between this quote and «kontrollert»/sending – computed once, used for the one-line summary and the panel. */
export function readiness({ vm, project, ctx, texts, risk, gate }) {
  const t = vm?.totals || {};
  const missing = (vm?.lines || []).flatMap((l) => [l, ...(l.children || [])]).filter((l) => l.price?.kind === "missing");
  const pending = (ctx.items || []).flatMap((i) => [i, ...(i.children || [])]).filter((i) => (t.pending_refs || []).includes(i.ref));
  const clarifications = (ctx.items || []).reduce((n, i) => n + (i.calc?.open_clarifications || 0) + (i.children || []).reduce((m, c) => m + (c.calc?.open_clarifications || 0), 0), 0);
  const unconfirmed = [["tender_deadline", "tilbudsfrist"], ["period_from", "leveringsperiode"]].filter(([k]) => project.facts?.[k] && basisOf(project, k) && basisOf(project, k) !== "bruker");
  const demoTexts = Object.values(texts || {}).filter((r) => r.blocks_control);
  const reviewTexts = Object.values(texts || {}).filter((r) => r.needs_review);
  const riskOpen = Boolean(risk && risk.required && !risk.ok);
  const reasons = [];
  if (missing.length) reasons.push(`kalkylen mangler pris på ${missing.length} ${missing.length === 1 ? "post" : "poster"}`);
  if (clarifications) reasons.push(`${clarifications} ${clarifications === 1 ? "avklaring er åpen" : "avklaringer er åpne"}`);
  if (pending.length) reasons.push(`${pending.length} ${pending.length === 1 ? "post er" : "poster er"} ikke ferdig vurdert`);
  unconfirmed.forEach(([, label]) => reasons.push(`${label} er ikke bekreftet`));
  if (demoTexts.length) reasons.push("en tekst er merket demo");
  if (reviewTexts.length) reasons.push("historisk tekst må gjennomgås");
  if (riskOpen) reasons.push(risk.stale ? "risikovurderingen er utdatert" : "risikosjekklisten er ikke fullført");
  (gate || []).filter((g) => !["preliminary", "risiko"].includes(g.key)).forEach((g) => reasons.push(g.text.charAt(0).toLowerCase() + g.text.slice(1)));
  return { missing, pending, clarifications, unconfirmed, demoTexts, reviewTexts, riskOpen, reasons, preliminary: Boolean(t.preliminary), project };
}

/** «Tilbudet er ikke klart til sending. Kalkylen mangler pris på 28 poster, og 2 avklaringer er åpne.» */
export function readinessSentence(reasons) {
  const head = reasons.slice(0, 3).map((r, i) => (i === 0 ? r.charAt(0).toUpperCase() + r.slice(1) : r));
  const more = reasons.length - head.length;
  const joined = head.length <= 1 ? head.join("") : head.length === 2 ? head.join(", og ") : `${head.slice(0, -1).join(", ")} og ${head[head.length - 1]}`;
  return `${joined}${more > 0 ? ` – og ${more} ${more === 1 ? "punkt" : "punkter"} til` : ""}.`;
}

function Item({ children, testId }) {
  return <li className="flex items-start gap-2.5 py-3 text-[14px] leading-[1.5]" data-testid={testId}><span className="mt-[8px] h-1.5 w-1.5 shrink-0 rounded-full bg-[hsl(var(--v-assumed))]" /><div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">{children}</div></li>;
}

/**
 * «Hva som mangler» – the concrete tasks before the quote can be controlled or sent, with a way to each of them. Docked
 * beside the paper when the surface is wide enough (the document stays in view), a sheet otherwise. The server enforces
 * the same controls either way; this is where they get room.
 */
export function TilbudControlSheet({ open, onOpenChange, r, gate, risk, ops, onGoto, onConfirm, onTextOp, revisionLabel, controlled = false }) {
  if (!open) return null;
  const n = r.reasons.length;
  const what = revisionLabel ? "versjonen" : "tilbudet";
  const title = !n ? "Alt er på plass" : controlled ? `${n} ${n === 1 ? "punkt har" : "punkter har"} endret seg etter kontrollen` : `${n} ${n === 1 ? "punkt" : "punkter"} før ${what} kan kontrolleres`;
  return (
    <Inspector onClose={() => onOpenChange(false)} eyebrow={`Hva som mangler${revisionLabel ? ` · ${revisionLabel}` : ""}`} title={title} sub="Kontroll her gir ingen fullmakt – og sender ingenting." testId="tilbud-control-sheet" label="Hva som mangler" noWide>
      {n ? (
        <ul className="divide-y divide-black/[0.05]" data-testid="tilbud-blockers">
          {r.missing.length ? (
            <Item testId="tilbud-blocker-missing">
              <span>{r.missing.length} {r.missing.length === 1 ? "post mangler pris" : "poster mangler pris"}</span>
              {r.missing.slice(0, 8).map((l) => <button key={l.id} type="button" onClick={() => onGoto(l.ref)} className={chip} data-testid={`tilbud-goto-${l.ref}`}>{l.ref}</button>)}
              {r.missing.length > 8 ? <span className="text-muted-foreground">+{r.missing.length - 8}</span> : null}
              <span className="text-muted-foreground">– pris dem i kalkylen, eller sett «Utgår» for det som ikke tilbys</span>
            </Item>
          ) : null}
          {r.pending.length ? (
            <Item testId="tilbud-blocker-pending">
              <span>{r.pending.length} {r.pending.length === 1 ? "post er" : "poster er"} priset, men ikke ferdig vurdert</span>
              {r.pending.slice(0, 8).map((i) => (
                <span key={i.id} className="inline-flex items-center gap-1 rounded-md bg-white px-1.5 py-0.5 text-[11.5px] shadow-[0_0_0_1px_hsl(212_40%_16%/0.08)]" data-testid={`tilbud-pending-${i.ref}`}>
                  <button type="button" onClick={() => onGoto(i.ref)} className="font-mono text-[hsl(var(--brand-strong))] hover:underline">{i.ref}</button>
                  <span className="text-muted-foreground">{i.calc?.open_clarifications ? `${i.calc.open_clarifications} avkl.` : "ny"}</span>
                  {!i.calc?.open_clarifications ? <button type="button" onClick={() => ops.patchItem(i.id, "status", "klar").catch(() => {})} className={mini} data-testid={`tilbud-mark-ready-${i.ref}`}>Merk klar</button> : null}
                </span>
              ))}
              {r.pending.length > 8 ? <span className="text-muted-foreground">+{r.pending.length - 8}</span> : null}
            </Item>
          ) : null}
          {r.unconfirmed.map(([k, label]) => (
            <Item key={k} testId={`tilbud-blocker-${k}`}>
              <span>{label[0].toUpperCase() + label.slice(1)} står i dokumentet, men er ikke bekreftet</span>
              <BasisMark basis={basisOf(r.project, k)} onConfirm={() => onConfirm(k === "period_from" ? ["period_from", "period_to"] : [k])} testId={`tilbud-basis-${k}`} />
            </Item>
          ))}
          {r.demoTexts.length ? (
            <Item testId="tilbud-blocker-demo-text">
              <span>Tekst merket demo/utkast: {r.demoTexts.map((x) => x.label).join(", ")}</span>
              <span className="text-muted-foreground">– kan eksporteres som utkast med fotnote, men versjonen kan ikke merkes kontrollert</span>
            </Item>
          ) : null}
          {r.reviewTexts.length ? (
            <Item testId="tilbud-blocker-review-text">
              <span>Historisk kildetekst må gjennomgås på saken</span>
              {r.reviewTexts.map((x) => (
                <span key={x.key} className="inline-flex items-center gap-1 rounded-md bg-white px-1.5 py-0.5 text-[11.5px] shadow-[0_0_0_1px_hsl(212_40%_16%/0.08)]" data-testid={`tilbud-review-${x.key}`}>
                  {x.label}
                  <button type="button" onClick={() => onTextOp(x.key, { reviewed: true })} className={mini} data-testid={`tilbud-review-${x.key}-btn`}>Gjennomgått</button>
                </span>
              ))}
            </Item>
          ) : null}
          {(gate || []).filter((g) => g.key !== "risiko").map((g) => (
            <li key={g.key} className="flex items-start justify-between gap-3 py-3 text-[14px] leading-[1.5]" data-testid={`tilbud-gate-${g.key}`}>
              <span className="flex min-w-0 items-start gap-2"><AlertTriangle className="mt-[3px] h-3.5 w-3.5 shrink-0 text-[hsl(var(--v-assumed))]" /><span>{g.text}</span></span>
              <button type="button" onClick={g.action.run} className="shrink-0 rounded-full bg-[hsl(var(--brand))] px-2.5 py-1 text-[12px] font-medium text-white hover:bg-[hsl(var(--brand-strong))]" data-testid={`tilbud-gate-${g.key}-action`}>{g.action.label}</button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="flex items-start gap-2.5 text-[14px] leading-[1.5] text-foreground" data-testid="tilbud-blockers-none"><Check className="mt-[3px] h-4 w-4 shrink-0 text-[hsl(var(--v-ok))]" strokeWidth={2.6} /> Ingen åpne punkter. Kalkylen er komplett, tekstene er gjennomgått og det som står i dokumentet er bekreftet.</p>
      )}
      {r.preliminary ? <p className="mt-3 text-[13px] leading-[1.5] text-muted-foreground">Summen er foreløpig – versjonen kan lages som internt utkast, men ikke kontrolleres før alt er priset.</p> : null}
      <div className="mt-6" data-testid="tilbud-control-risk"><RisikoCard risk={risk} /></div>
    </Inspector>
  );
}
